import { Component, Input, OnInit } from '@angular/core';

import { Property, PropertySet } from '@mirage-projects/cw-models';
import { DbPcvizService } from '@mirage-projects/cw-pcviz-core';
import { NumberHelperService } from '@mirage-projects/cw-utils';

import { PropertyBagService } from '../property-bag.service';

/**
 * Component for the point array property bag property
 */
@Component({
    selector: 'cw-property-point-array',
    templateUrl: './property-point-array.component.html',
    styleUrls: ['./property-point-array.component.scss'],
    standalone: false,
})
export class PropertyPointArrayComponent implements OnInit {
    /** This object comes from the core and provides methods to obtain and set the value of the properties */
    @Input() propertySet!: PropertySet;

    /** Input field with the required information for this field type */
    @Input() property!: Property;

    fieldType = 'PointArrayField';
    decimals = 3;
    label = '';

    constructor(
        private dbPcvizService: DbPcvizService,
        private propertyBagService: PropertyBagService,
        private numberHelperService: NumberHelperService
    ) {}

    ngOnInit(): void {
        this.label = this.propertyBagService.getCorrectLabel(this.property) ?? '';

        const listenerController = this.dbPcvizService.getListenerController();
        const preferencesController = this.dbPcvizService.getControllerDB('UserPreferences');
        const numberDecimalsKey = this.dbPcvizService.getUtils().NUMBER_OF_DECIMALS_KEY;

        this.decimals = preferencesController.getPreference(numberDecimalsKey);

        preferencesController.registerListener(listenerController.PROJECT, numberDecimalsKey, (numberDecimals: number) => {
            this.decimals = numberDecimals;
        });

        this.property.value = this.propertyBagService.getValueProperty(this.propertySet, this.property);
    }

    /**
     * Change value of the point array, to be used from view
     * @param coordIndex  The index of the point coordinate to change
     * @param pointArrayIndex The index of the point to change
     * @param value Value to set
     */
    changeField(coordIndex: number, pointArrayIndex: number, value: number): void {
        this.property.value[pointArrayIndex][coordIndex] = value;
        this.propertySet.setProperty(this.property.field, this.property.value, pointArrayIndex);
        this.propertyBagService.emmitPropertyValue(this.property);
    }

    /**
     *  Select a vertex from the point array, to be used from view
     * @param iVertex The index of the vertex to select
     */
    selectVertex(iVertex: number): void {
        this.propertySet.changeSelectedPropertyField(iVertex);
        const selectionObjectController = this.dbPcvizService.getControllerDB('SelectionObject');
        selectionObjectController.getSelectionObject().setVertex(iVertex);
        // Call to notify is necessary because we've changed the vertex in the selection object and
        // the InformationTool isn't executing setSelectionObject each frame.
        selectionObjectController.notify();
    }

    /**
     * Get the three visible values of a point
     * @param point The point to get the visible values from
     */
    getVisiblePointValues(point: number[]): number[] {
        return point.slice(0, 3) ?? [];
    }

    /**
     *  Get the value of a point coordinate
     * @param point The point to get the coordinate from
     * @param coordIndex The index of the coordinate to get
     */
    getPointCoord(point: number[], coordIndex: number): string {
        const coord = +(point?.[coordIndex] ?? 0);
        return this.numberHelperService.toFixedWithPrecision(coord, this.decimals);
    }
}
